/**
 * Abrir la cuenta de un proveedor, o corregirla.
 *
 * Es el único lugar donde se toca la cuenta sin una compra ni un pago detrás, y
 * por eso pide más que los otros diálogos: la moneda es obligatoria y no tiene
 * default. En un sistema bimonetario, adivinarla es elegir cuánto se debe.
 *
 * LOS CUATRO TIPOS, Y POR QUÉ CADA UNO DICE PARA QUÉ LADO VA
 * ---------------------------------------------------------
 * En la cuenta de clientes, `AJUSTE+` *reduce* la deuda y `AJUSTE-` la aumenta,
 * y hay un comentario en la migración explicándolo porque no se entiende solo.
 * Acá el texto de cada opción dice qué hace, así nadie tiene que recordarlo:
 *
 *   Saldo inicial   — lo que ya se le debía antes de usar el sistema
 *   Le debo más     — una diferencia que apareció, un flete que no estaba
 *   Le debo menos   — una condonación, un error que se corrige
 *   Nota de crédito — el proveedor acredita algo (una devolución)
 *
 * Los dos últimos se imputan FIFO contra lo más viejo, igual que un pago.
 */
import { useEffect, useState } from "react";
import { Check } from "lucide-react";

import { type Moneda, parsearImporte } from "@tora/shared";

import { Aviso } from "@/components/ui/aviso";
import { Button } from "@/components/ui/button";
import { Campo } from "@/components/catalogo/Campos";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import type { DatosAjuste } from "./consultas";

type TipoAjuste = DatosAjuste["tipo"];

const TIPOS: ReadonlyArray<{ valor: TipoAjuste; label: string; ayuda: string }> = [
  {
    valor: "SALDO_INICIAL",
    label: "Saldo inicial",
    ayuda: "Lo que ya le debías antes de empezar a cargar las compras acá. Suma deuda.",
  },
  {
    valor: "AJUSTE-",
    label: "Le debo más",
    ayuda: "Una diferencia que apareció: un flete, un recargo, algo que no estaba en la factura.",
  },
  {
    valor: "AJUSTE+",
    label: "Le debo menos",
    ayuda:
      "Una condonación o un error que se corrige. Cancela deuda, de la más vieja a la más nueva.",
  },
  {
    valor: "NC",
    label: "Nota de crédito",
    ayuda: "El proveedor te acredita algo, por una devolución o un faltante. Cancela deuda.",
  },
];

export interface DialogAjusteProveedorProps {
  abierto: boolean;
  nombre: string;
  guardando?: boolean;
  onCerrar: () => void;
  onConfirmar: (datos: DatosAjuste) => void;
}

export function DialogAjusteProveedor({
  abierto,
  nombre,
  guardando = false,
  onCerrar,
  onConfirmar,
}: DialogAjusteProveedorProps): JSX.Element {
  const [tipo, setTipo] = useState<TipoAjuste>("SALDO_INICIAL");
  const [importe, setImporte] = useState("");
  // Sin default a propósito: elegir la moneda es parte de decir cuánto se debe.
  const [moneda, setMoneda] = useState<Moneda | "">("");
  const [detalle, setDetalle] = useState("");

  useEffect(() => {
    if (!abierto) return;
    setTipo("SALDO_INICIAL");
    setImporte("");
    setMoneda("");
    setDetalle("");
  }, [abierto]);

  // Ver el comentario de `DialogPagoProveedor`: en Argentina el punto es el
  // separador de miles y `Number("1.590")` da 1,59.
  const num = parsearImporte(importe) ?? NaN;
  const importeValido = Number.isFinite(num) && num > 0;
  const puedeConfirmar = importeValido && moneda !== "";

  const elegido = TIPOS.find((t) => t.valor === tipo);

  return (
    <Dialog open={abierto} onOpenChange={(v) => !v && onCerrar()}>
      <DialogContent tamano="md" mobile="hoja">
        <DialogHeader>
          <DialogTitle>Ajustar la cuenta de {nombre}</DialogTitle>
          <DialogDescription>Queda asentado con fecha de hoy y deja rastro.</DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-4">
          <Campo label="Qué es" id="ajuste-tipo" ayuda={elegido?.ayuda}>
            <Select value={tipo} onValueChange={(v) => setTipo(v as TipoAjuste)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TIPOS.map((t) => (
                  <SelectItem key={t.valor} value={t.valor}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Campo>

          <Campo
            label="Importe"
            id="ajuste-importe"
            ayuda="Siempre positivo: el tipo de arriba decide de qué lado va."
          >
            <Input
              id="ajuste-importe"
              type="text"
              inputMode="decimal"
              value={importe}
              onChange={(e) => setImporte(e.target.value)}
              className="w-40 tabular-nums"
              placeholder="0,00"
            />
          </Campo>

          <Campo label="En qué moneda" id="ajuste-moneda">
            <Select value={moneda} onValueChange={(v) => setMoneda(v as Moneda)}>
              <SelectTrigger>
                <SelectValue placeholder="Elegí una" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="USD">Dólares</SelectItem>
                <SelectItem value="ARS">Pesos</SelectItem>
              </SelectContent>
            </Select>
          </Campo>

          <Campo
            label="Nota"
            id="ajuste-detalle"
            ayuda="Para acordarte en dos meses de qué fue esto."
          >
            <Input
              id="ajuste-detalle"
              type="text"
              value={detalle}
              onChange={(e) => setDetalle(e.target.value)}
              maxLength={200}
            />
          </Campo>

          {importe !== "" && !importeValido ? (
            <Aviso tono="destructive">El importe tiene que ser un número mayor a cero.</Aviso>
          ) : null}
        </DialogBody>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onCerrar} disabled={guardando}>
            Cancelar
          </Button>
          <Button
            type="button"
            disabled={!puedeConfirmar || guardando}
            onClick={() =>
              onConfirmar({
                tipo,
                importe: num,
                moneda: moneda as Moneda,
                ...(detalle.trim() ? { detalle: detalle.trim() } : {}),
              })
            }
          >
            <Check aria-hidden="true" />
            Asentar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
